<app-organization-sidebar-layout>
  <app-page-header>
    <div class="flex flex-col gap-2.5">
      <div class="flex gap-3 items-center">
        <ui-button
          [iconOnly]="true"
          mode="outlined"
          class="flex-none"
          [routerLink]="['/organizations', organizationId(), 'analysis']"
          data-track="price-back-button"
        >
          <ng-icon name="heroArrowLeft" size="20" class="!block" />
        </ui-button>
        <app-page-title class="flex-1">
          {{ 'cockpit.analysis.hpfc.title' | transloco }}
        </app-page-title>
      </div>
      <ui-breadcrumb-nav
        [ariaLabel]="'cockpit.analysis.hpfc.detail.breadcrumbAriaLabel' | transloco"
      >
        <ui-breadcrumb>
          <a [routerLink]="['/organizations', organizationId()]">
            <ng-icon name="heroHome" size="20" class="!block" />
            <span class="sr-only">{{
              'cockpit.analysis.hpfc.detail.breadcrumbHome' | transloco
            }}</span>
          </a>
        </ui-breadcrumb>
        <ui-breadcrumb>
          <a [routerLink]="['/organizations', organizationId(), 'analysis']">
            {{ 'cockpit.analysis.hpfc.detail.breadcrumbAnalysis' | transloco }}
          </a>
        </ui-breadcrumb>
        <ui-breadcrumb>
          <a
            [routerLink]="['/organizations', organizationId(), 'analysis', 'price']"
            aria-current="page"
          >
            {{ 'cockpit.analysis.hpfc.detail.breadcrumbPrices' | transloco }}
          </a>
        </ui-breadcrumb>
      </ui-breadcrumb-nav>
    </div>
  </app-page-header>

  <div class="flex flex-col gap-5">
    <div class="flex gap-3 items-center">
      <app-analysis-price-comparison-select
        class="flex-1 block"
        [currentFilter]="filter()"
        (selectChange)="onChangeHistoricYear($event)"
      />
      <ui-switch-bar
        [formField]="filterForm.view"
        class="flex-none w-[78px] inline-block"
        (valueChange)="onChangeView($event)"
        data-track="price-view-switch-bar"
      >
        <ui-switch-bar-option value="graph">
          <ng-icon name="heroArrowTrendingUp" size="20" class="!block" />
        </ui-switch-bar-option>
        <ui-switch-bar-option value="table">
          <ng-icon name="heroListBullet" size="20" class="!block" />
        </ui-switch-bar-option>
      </ui-switch-bar>
    </div>

    <div class="px-6 py-5 border border-outline dark:border-outline-darkmode rounded-md">
      <app-analysis-price-filters [filter]="filter()" (changeFilter)="onChangeFilter($event)" />

      @switch (filter().view) {
        @case ('graph') {
          <app-analysis-price-graph-chart
            class="block h-120"
            [prices]="data()?.data ? data()!.data!.entries : []"
            [comparisonPrices]="comparisonPrices()"
            [comparisonYear]="filter().compareHistoricYear"
            [granularity]="granularity()"
            [showEurMwh]="showEurMwh()"
            [loading]="data()?.isLoading ?? true"
            [error]="data()?.error"
            (clickReload)="onClickReload()"
          />
        }
        @case ('table') {
          <app-analysis-price-table
            class="block"
            [prices]="data()?.data ? data()!.data!.entries : []"
            [granularity]="granularity()"
            [showEurMwh]="showEurMwh()"
            [loading]="data()?.isLoading ?? true"
            [error]="data()?.error"
            (clickReload)="onClickReload()"
          />
        }
      }
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
      @if (data()?.isLoading) {
        <ng-container [ngTemplateOutlet]="loadingKpiCard" />
        <ng-container [ngTemplateOutlet]="loadingKpiCard" />
      } @else {
        <div
          class="rounded-lg bg-surface-magic dark:bg-surface-magic-darkmode border border-outline dark:border-outline-darkmode"
          data-track="price-kpi-card-min"
        >
          @if (data()?.error) {
            <div class="px-6 py-5">
              <app-error-component />
            </div>
          } @else if (data()?.data) {
            <h3
              class="px-6 py-5 font-semibold leading-6 border-b border-outline dark:border-outline-darkmode"
            >
              {{ 'cockpit.analysis.hpfc.detail.minPricesTitle' | transloco }}
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 py-5">
              <span class="text-sm leading-6 font-semibold">
                {{ 'cockpit.analysis.hpfc.detail.lowestPrice' | transloco }}
              </span>
              <span
                class="text-sm leading-6 text-right text-on-surface-subtle dark:text-on-surface-subtle-darkmode"
              >
                {{
                  data()!.data!.pricesSummary.minPrice | convertPrice: showEurMwh() | germanNumber
                }}
                {{
                  (showEurMwh()
                    ? 'cockpit.analysis.hpfc.detail.yAxisLabelEurMwh'
                    : 'cockpit.analysis.hpfc.detail.yAxisLabel'
                  ) | transloco
                }}
              </span>
              <span class="text-sm leading-6 font-semibold">
                {{ 'cockpit.analysis.hpfc.detail.withoutOutliers' | transloco }}
              </span>
              <span
                class="text-sm leading-6 text-right text-on-surface-subtle dark:text-on-surface-subtle-darkmode"
              >
                {{
                  data()!.data!.pricesSummary.minPriceWithoutOutliers
                    | convertPrice: showEurMwh()
                    | germanNumber
                }}
                {{
                  (showEurMwh()
                    ? 'cockpit.analysis.hpfc.detail.yAxisLabelEurMwh'
                    : 'cockpit.analysis.hpfc.detail.yAxisLabel'
                  ) | transloco
                }}
              </span>
            </div>
          }
        </div>
        <div
          class="rounded-lg bg-surface-magic dark:bg-surface-magic-darkmode border border-outline dark:border-outline-darkmode"
          data-track="price-kpi-card-max"
        >
          @if (data()?.error) {
            <div class="px-6 py-5">
              <app-error-component />
            </div>
          } @else if (data()?.data) {
            <h3
              class="px-6 py-5 font-semibold leading-6 border-b border-outline dark:border-outline-darkmode"
            >
              {{ 'cockpit.analysis.hpfc.detail.maxPricesTitle' | transloco }}
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 py-5">
              <span class="text-sm leading-6 font-semibold">
                {{ 'cockpit.analysis.hpfc.detail.highestPrice' | transloco }}
              </span>
              <span
                class="text-sm leading-6 text-right text-on-surface-subtle dark:text-on-surface-subtle-darkmode"
              >
                {{
                  data()!.data!.pricesSummary.maxPrice | convertPrice: showEurMwh() | germanNumber
                }}
                {{
                  (showEurMwh()
                    ? 'cockpit.analysis.hpfc.detail.yAxisLabelEurMwh'
                    : 'cockpit.analysis.hpfc.detail.yAxisLabel'
                  ) | transloco
                }}
              </span>
              <span class="text-sm leading-6 font-semibold">
                {{ 'cockpit.analysis.hpfc.detail.withoutOutliers' | transloco }}
              </span>
              <span
                class="text-sm leading-6 text-right text-on-surface-subtle dark:text-on-surface-subtle-darkmode"
              >
                {{
                  data()!.data!.pricesSummary.maxPriceWithoutOutliers
                    | convertPrice: showEurMwh()
                    | germanNumber
                }}
                {{
                  (showEurMwh()
                    ? 'cockpit.analysis.hpfc.detail.yAxisLabelEurMwh'
                    : 'cockpit.analysis.hpfc.detail.yAxisLabel'
                  ) | transloco
                }}
              </span>
            </div>
          }
        </div>
      }
    </div>
  </div>
</app-organization-sidebar-layout>

<ng-template #loadingKpiCard>
  <div
    class="rounded-lg bg-surface-magic dark:bg-surface-magic-darkmode border border-outline dark:border-outline-darkmode"
  >
    <h3
      class="px-6 py-5 font-semibold leading-6 border-b border-outline dark:border-outline-darkmode"
    >
      <ui-skeleton class="block w-full rounded-full h-5" />
    </h3>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-5 px-6 py-5">
      <ui-skeleton class="w-full rounded-full h-5" />
      <ui-skeleton class="w-full rounded-full h-5" />
      <ui-skeleton class="w-full rounded-full h-5" />
      <ui-skeleton class="w-full rounded-full h-5" />
    </div>
  </div>
</ng-template>
